import React, { useState, useEffect, useMemo, useRef } from 'react';
import { verifyIncidentLocation } from '../services/geminiService';
import { Incident, ThreatLevel } from '../types';
import { incidentsForPanel } from '../services/incidentCategory';
import { ShieldAlert, Radio, MapPin, Clock, FileText, Loader2, X, Maximize2, Minimize2, Ghost, Eye, ExternalLink, AlertTriangle, CheckCircle, HelpCircle, ChevronDown, Zap } from 'lucide-react';
import IncidentComments from './IncidentComments';
import IntelTab from './IntelTab';
import CityPulse from './CityPulse';
import { VerificationControls } from './VerificationControls';
import { PhotoUploader } from './PhotoUploader';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface IncidentLogProps {
  incidents: Incident[];
  onSelect: (inc: Incident) => void;
  onSummarize: (incidentId: string, dispatchText: string) => Promise<void>;
  summarizingIncidentId: string | null;
  globalBackoffActive: boolean;
  onClose: () => void;
  selectedIncident: Incident | null;
  vicinityReport: string;
  theme: {
    primary: string;
    danger: string;
    bg: string;
    header: string;
    border: string;
    accent: string;
  };
  isDetached?: boolean;
  isGhost?: boolean;
  logGhostOpacity?: number;
  onToggleDetach?: () => void;
  onToggleGhost?: () => void;
  onSetGhostOpacity?: (opacity: number) => void;
  onPopOut?: () => void;
  activeTab?: 'threats' | 'alerts' | 'intel' | 'pulse';
  onTabChange?: (tab: 'threats' | 'alerts' | 'intel' | 'pulse') => void;
  currentUserId: string | null;
  userLocation: { latitude: number; longitude: number } | null;
  highlightedIncidentId?: string | null;
  onVenueClick?: (lat: number, lng: number, name: string) => void;
  onOpenSponsorModal?: () => void;
}

// --- Sort-mode helpers for threats side panel (added 2026-08-25) ---
// Haversine distance in kilometers between two {lat,lng} points.
// Client-side sort so we avoid any schema/geo-sqlite change on the backend.
const haversineKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * s2 * s2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};
// Tucson center fallback if the user denies geolocation.
const TUCSON_CENTER = { lat: 32.2226, lng: -110.9747 };
type ThreatsSortMode = "risk" | "newest" | "closest";
const THREATS_SORT_LS_KEY = "tucsonstreets.threats.sortMode";
// Alerts panel gets its own sort override, defaulting to the same closest+newest composite as Threats.
type AlertsSortMode = "urgent" | "newest" | "closest";
const ALERTS_SORT_LS_KEY = "tucsonstreets.alerts.sortMode";

const IncidentLog: React.FC<IncidentLogProps> = ({ 
  incidents, 
  onSelect, 
  onSummarize, 
  summarizingIncidentId, 
  globalBackoffActive, 
  onClose,
  selectedIncident,
  vicinityReport,
  theme,
  isDetached = false,
  isGhost = false,
  logGhostOpacity = 75,
  onToggleDetach,
  onToggleGhost,
  onSetGhostOpacity,
  onPopOut,
  activeTab: controlledActiveTab,
  onTabChange,
  currentUserId,
  userLocation,
  highlightedIncidentId,
  onVenueClick,
  onOpenSponsorModal,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [internalActiveTab, setInternalActiveTab] = useState<'threats' | 'alerts' | 'intel'>('threats');
  const [filterThreatLevel, setFilterThreatLevel] = useState<string>('ALL');
  const [threatsSortMode, setThreatsSortModeRaw] = useState<ThreatsSortMode>(() => {
    try {
      const v = localStorage.getItem(THREATS_SORT_LS_KEY) as ThreatsSortMode | null;
      if (v === "risk" || v === "newest" || v === "closest") return v;
    } catch {}
    return "risk";
  });
  const setThreatsSortMode = (m: ThreatsSortMode) => {
    setThreatsSortModeRaw(m);
    try { localStorage.setItem(THREATS_SORT_LS_KEY, m); } catch {}
  };

  // Alerts panel sort state (independent of Threats so the user can tune each panel).
  // Default "urgent" = closest + newest composite so the NEAREST + FRESHEST alert floats to the top.
  const [alertsSortMode, setAlertsSortModeRaw] = useState<AlertsSortMode>(() => {
    try {
      const v = localStorage.getItem(ALERTS_SORT_LS_KEY) as AlertsSortMode | null;
      if (v === "urgent" || v === "newest" || v === "closest") return v;
    } catch {}
    return "urgent";
  });
  const setAlertsSortMode = (m: AlertsSortMode) => {
    setAlertsSortModeRaw(m);
    try { localStorage.setItem(ALERTS_SORT_LS_KEY, m); } catch {}
  };

  const [filterSource, setFilterSource] = useState<string>('ALL');
  const [verification, setVerification] = useState<{ text: string, sources: any[] } | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const activeTab = controlledActiveTab || internalActiveTab;
  const setActiveTab = onTabChange || setInternalActiveTab;

  const calculateRiskScore = (inc: Incident) => {
    let score = 0;
    // Threat Level Weight
    if (inc.threatLevel === ThreatLevel.CRITICAL) score += 1000;
    else if (inc.threatLevel === ThreatLevel.HIGH) score += 500;
    else score += 100;
    
    // Distance Weight (closer is higher risk)
    score += (1 / ((inc.distance || 0) + 0.1)) * 50;
    
    // Time Weight (newer is higher risk)
    score += (inc.timestamp / Date.now()) * 100;
    
    return score;
  };

  const sortedIncidents = useMemo(() => {
    if (!incidents) return [];
    // 1. Auto-categorize into Threats vs Alerts vs Intel via the classifier.
    //    Live TPD dispatch co-populates BOTH Threats and Alerts by default
    //    (operator mission item #1). See services/incidentCategory.ts.
    let forPanel = (activeTab === 'threats' || activeTab === 'alerts')
      ? incidentsForPanel(incidents, activeTab)
      : incidents;
    // Fail-safe: If classifier yields 0 items but we have live dispatch incidents, display them!
    if (forPanel.length === 0 && incidents.length > 0) {
      forPanel = incidents;
    }
    console.log('[IncidentLog] incidents:', incidents.length, 'forPanel:', forPanel.length, 'activeTab:', activeTab);
    // 2. Then apply the user filter pills (level + source).
    let filtered = forPanel.filter(inc => {
      const matchesThreat = filterThreatLevel === 'ALL' || inc.threatLevel === filterThreatLevel;
      const matchesSource = filterSource === 'ALL' || inc.source === filterSource;
      return matchesThreat && matchesSource;
    });
    // 3. Unified default sort helper: closest + newest composite.
    //    Proximity is weighted heavier than time so the NEAREST hazard
    //    always beats a far-away but slightly-newer call.
    const origin = userLocation
      ? { lat: userLocation.latitude, lng: userLocation.longitude }
      : TUCSON_CENTER;
    const now = Date.now();
    const closestNewestScore = (inc: Incident): number => {
      const distKm = haversineKm(origin, inc.location);
      // Distance penalty: -60 per km (so 1km away = -60, 10km = -600).
      const distScore = -distKm * 60;
      // Freshness bonus: +1 per minute of newness, capped at last 2h.
      const ageMin = Math.max(0, (now - (inc.timestamp || now)) / 60000);
      const freshScore = Math.max(0, 120 - ageMin);
      // Critical + High get a small bump so a nearby homicide beats a nearby traffic stop.
      const levelBump = inc.threatLevel === ThreatLevel.CRITICAL ? 200
                      : inc.threatLevel === ThreatLevel.HIGH     ? 100
                      : inc.threatLevel === ThreatLevel.MEDIUM   ?  25 : 0;
      return distScore + freshScore + levelBump;
    };

    console.log('[IncidentLog] filtered:', filtered.length);
    if (activeTab === 'threats') {
      if (threatsSortMode === "newest") {
        return [...filtered].sort((a, b) => b.timestamp - a.timestamp);
      }
      if (threatsSortMode === "closest") {
        return [...filtered].sort((a, b) => haversineKm(origin, a.location) - haversineKm(origin, b.location));
      }
      // "risk" default -> new closest+newest composite (operator's stated default).
      return [...filtered].sort((a, b) => closestNewestScore(b) - closestNewestScore(a));
    }
    if (activeTab === 'alerts') {
      if (alertsSortMode === "newest") {
        return [...filtered].sort((a, b) => b.timestamp - a.timestamp);
      }
      if (alertsSortMode === "closest") {
        return [...filtered].sort((a, b) => haversineKm(origin, a.location) - haversineKm(origin, b.location));
      }
      // "urgent" default -> unified closest+newest composite (matches Threats default).
      return [...filtered].sort((a, b) => closestNewestScore(b) - closestNewestScore(a));
    }
    // Intel/other tabs still use pure newest fallback.
    return [...filtered].sort((a, b) => b.timestamp - a.timestamp);
  }, [incidents, activeTab, filterThreatLevel, filterSource, threatsSortMode, alertsSortMode, userLocation?.latitude, userLocation?.longitude]);

  const handlePopOut = () => {
    const url = window.location.origin + window.location.pathname + `?mode=log&isGhost=${isGhost}&opacity=${logGhostOpacity}`;
    window.open(url, 'IncidentLog', 'width=450,height=800,menubar=no,toolbar=no,location=no,status=no');
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [sortedIncidents.length, activeTab]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    setShowScrollBottom(scrollHeight - scrollTop - clientHeight > 100);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setVerification(null);
    setIsVerifying(false);
  }, [selectedIncident]);

  return (
    <div className={`flex flex-col h-full overflow-hidden transition-all duration-300 ${!isDetached ? 'border-l' : ''}`}
      style={{
        backgroundColor: isGhost ? `rgba(0, 0, 0, ${logGhostOpacity / 100})` : '#000000',
        borderColor: '#d4a843',
        backdropFilter: isGhost ? 'blur(8px)' : 'none'
      }}>
      <div className="p-3 border-b flex items-center justify-between shrink-0" style={{ borderColor: '#d4a843', backgroundColor: '#000000' }}>
        <div className="flex items-center gap-3">
          <h2 className="font-orbitron text-[10px] font-bold tracking-widest flex items-center gap-2 text-[#d4a843]">
            <Radio size={14} /> TUCSON STREETS DISPATCH
          </h2>
          <div className="text-[9px] font-mono text-[#d4a843] bg-black px-2 py-0.5 rounded border border-[#d4a843]/50">
            {currentTime.toLocaleTimeString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isGhost && onSetGhostOpacity && (
            <input
              type="range"
              min="10"
              max="100"
              value={logGhostOpacity}
              onChange={(e) => onSetGhostOpacity(Number(e.target.value))}
              className="w-16 h-1 bg-black rounded-lg appearance-none cursor-pointer accent-[#d4a843]"
            />
          )}
          {!isDetached && (
            <button
              onClick={handlePopOut}
              className="p-1 rounded text-[#d4a843] hover:bg-[#d4a843]/20 transition-colors"
              title="Pop out to independent window"
            >
              <ExternalLink size={14} />
            </button>
          )}
          {onToggleGhost && (
            <button
              onClick={onToggleGhost}
              className={`p-1 rounded transition-colors text-[#d4a843] ${isGhost ? 'bg-[#d4a843]/20' : 'hover:bg-[#d4a843]/20'}`}
              title={isGhost ? "Disable Ghost Mode" : "Enable Ghost Mode"}
            >
              <Ghost size={14} />
            </button>
          )}
          {onToggleDetach && (
            <button
              onClick={onToggleDetach}
              className="p-1 rounded text-[#d4a843] hover:bg-[#d4a843]/20 transition-colors"
              title={isDetached ? "Attach to Sidebar" : "Detach Window"}
            >
              {isDetached ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded text-[#d4a843] hover:bg-[#d4a843]/20 transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="flex border-b border-[#d4a843]/50 bg-black">
        <button
          onClick={() => setActiveTab('threats')}
          className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 font-mono text-[#d4a843] ${activeTab === 'threats' ? 'bg-[#d4a843]/20 border-b-2 border-[#d4a843]' : 'hover:bg-[#d4a843]/10'}`}
        >
          <ShieldAlert size={12} /> Threats
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 font-mono text-[#d4a843] ${activeTab === 'alerts' ? 'bg-[#d4a843]/20 border-b-2 border-[#d4a843]' : 'hover:bg-[#d4a843]/10'}`}
        >
          <AlertTriangle size={12} /> Alerts
        </button>
        <button
          onClick={() => setActiveTab('intel')}
          className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 font-mono text-[#d4a843] ${activeTab === 'intel' ? 'bg-[#d4a843]/20 border-b-2 border-[#d4a843]' : 'hover:bg-[#d4a843]/10'}`}
        >
          <FileText size={12} /> Intel
        </button>
        <button
          onClick={() => setActiveTab('pulse')}
          className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 font-mono text-[#d4a843] ${activeTab === 'pulse' ? 'bg-[#d4a843]/20 border-b-2 border-[#d4a843]' : 'hover:bg-[#d4a843]/10'}`}
        >
          <Zap size={12} /> Pulse
        </button>
      </div>

      {activeTab === 'threats' && (
        <div className="px-2 pt-2 pb-1 border-b border-[#d4a843]/30 bg-black flex items-center gap-1">
          <span className="text-[9px] font-mono uppercase tracking-widest text-[#d4a843]/70 mr-1">Sort</span>
          <button
            onClick={() => setThreatsSortMode("risk")}
            title="Legacy composite: threat level + freshness + proximity."
            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest font-mono rounded-sm border transition-colors ${threatsSortMode === "risk" ? "bg-[#d4a843]/25 text-[#d4a843] border-[#d4a843]" : "bg-black text-[#d4a843]/70 border-[#d4a843]/40 hover:bg-[#d4a843]/10"}`}
          >Risk</button>
          <button
            onClick={() => setThreatsSortMode("newest")}
            title="Most recent report at top, descending by timestamp."
            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest font-mono rounded-sm border transition-colors ${threatsSortMode === "newest" ? "bg-[#d4a843]/25 text-[#d4a843] border-[#d4a843]" : "bg-black text-[#d4a843]/70 border-[#d4a843]/40 hover:bg-[#d4a843]/10"}`}
          >Newest</button>
          <button
            onClick={() => { if (userLocation) setThreatsSortMode("closest"); }}
            disabled={!userLocation}
            title={userLocation ? "Nearest incident to your location at top." : "Enable location access to sort by distance from you."}
            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest font-mono rounded-sm border transition-colors ${!userLocation ? "bg-black text-[#d4a843]/25 border-[#d4a843]/20 cursor-not-allowed" : threatsSortMode === "closest" ? "bg-[#d4a843]/25 text-[#d4a843] border-[#d4a843]" : "bg-black text-[#d4a843]/70 border-[#d4a843]/40 hover:bg-[#d4a843]/10"}`}
          >Closest</button>
        </div>
      )}
      {activeTab === 'alerts' && (
        <div className="px-2 pt-2 pb-1 border-b border-[#d4a843]/30 bg-black flex items-center gap-1">
          <span className="text-[9px] font-mono uppercase tracking-widest text-[#d4a843]/70 mr-1">Sort</span>
          <button
            onClick={() => setAlertsSortMode("urgent")}
            title="Default: closest + freshest alerts weighted together so the nearest, most-recent item is at the top."
            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest font-mono rounded-sm border transition-colors ${alertsSortMode === "urgent" ? "bg-[#d4a843]/25 text-[#d4a843] border-[#d4a843]" : "bg-black text-[#d4a843]/70 border-[#d4a843]/40 hover:bg-[#d4a843]/10"}`}
          >Urgent</button>
          <button
            onClick={() => setAlertsSortMode("newest")}
            title="Most recent alert first, ignoring proximity."
            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest font-mono rounded-sm border transition-colors ${alertsSortMode === "newest" ? "bg-[#d4a843]/25 text-[#d4a843] border-[#d4a843]" : "bg-black text-[#d4a843]/70 border-[#d4a843]/40 hover:bg-[#d4a843]/10"}`}
          >Newest</button>
          <button
            onClick={() => { if (userLocation) setAlertsSortMode("closest"); }}
            disabled={!userLocation}
            title={userLocation ? "Nearest alert to your location at top." : "Enable location access to sort by distance from you."}
            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-widest font-mono rounded-sm border transition-colors ${!userLocation ? "bg-black text-[#d4a843]/25 border-[#d4a843]/20 cursor-not-allowed" : alertsSortMode === "closest" ? "bg-[#d4a843]/25 text-[#d4a843] border-[#d4a843]" : "bg-black text-[#d4a843]/70 border-[#d4a843]/40 hover:bg-[#d4a843]/10"}`}
          >Closest</button>
        </div>
      )}
      <div className="p-2 border-b border-[#d4a843]/50 bg-black flex gap-2">
        <select 
          value={filterThreatLevel} 
          onChange={(e) => setFilterThreatLevel(e.target.value)}
          className="flex-1 terminal-input text-[9px] p-1 rounded-sm uppercase tracking-widest"
        >
          <option value="ALL">ALL THREATS</option>
          <option value="LOW">LOW</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="HIGH">HIGH</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>
        <select 
          value={filterSource} 
          onChange={(e) => setFilterSource(e.target.value)}
          className="flex-1 terminal-input text-[9px] p-1 rounded-sm uppercase tracking-widest"
        >
          <option value="ALL">ALL SOURCES</option>
          <option value="RADIO">RADIO</option>
          <option value="DISPATCH">DISPATCH</option>
          <option value="USER_REPORT">USER REPORT</option>
          <option value="CRIME_RADAR">CRIME RADAR</option>
        </select>
      </div>

      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar relative"
      >
        {activeTab === 'pulse' ? (
          <CityPulse
            userLocation={userLocation ? { lat: userLocation.latitude, lng: userLocation.longitude } : null}
            onEventClick={(lat, lon, name) => onVenueClick?.(lat, lon, name ?? '')}
          />
        ) : activeTab === 'intel' ? (
          <IntelTab theme={theme} userLocation={userLocation ? { lat: userLocation.latitude, lng: userLocation.longitude } : null} />
        ) : (!incidents || incidents.length === 0) ? (
          <div className="flex flex-col items-center justify-center h-full text-[#d4a843] opacity-80">
            <Loader2 size={32} className="animate-spin mb-3 text-[#00e5ff]" />
            <p className="text-[10px] uppercase text-center tracking-widest font-mono">SYNCING LIVE CAD DISPATCH FEED...</p>
            <p className="text-[8px] text-[#d4a843]/60 font-mono mt-1">Connecting to Tucson P25 telemetry</p>
          </div>
        ) : sortedIncidents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#d4a843] opacity-60">
            <ShieldAlert size={40} />
            <p className="mt-4 text-[10px] uppercase text-center tracking-widest font-mono">NO ACTIVE {activeTab.toUpperCase()}</p>
          </div>
        ) : (
          sortedIncidents.map((inc, incIdx) => (
            <React.Fragment key={inc.id}>
              {incIdx === 2 && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenSponsorModal) onOpenSponsorModal();
                  }}
                  className="my-3 p-3.5 rounded-xl border border-[#ffd700]/70 bg-gradient-to-b from-[#141e30] to-[#080d18] shadow-[0_0_20px_rgba(255,215,0,0.18)] cursor-pointer hover:border-[#ffd700] hover:scale-[1.01] transition-all font-mono"
                >
                  <div className="flex items-center justify-between pb-1.5 border-b border-[#ffd700]/30 text-[9px]">
                    <div className="flex items-center gap-1.5 text-[#ffd700] font-bold">
                      <span className="w-2 h-2 rounded-full bg-[#ffd700] animate-ping" />
                      <span>★ SPONSORED BEACON · $25/MO</span>
                    </div>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-extrabold uppercase">3D RADAR ACTIVE</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs font-bold text-white font-orbitron">🍕 Rocco's Little Chicago</span>
                    <span className="text-[10px] text-amber-400 font-bold">BROADWAY &amp; 4TH</span>
                  </div>
                  <p className="text-[11px] text-gray-300 mt-1 leading-snug">
                    Voted Best Deep Dish. Open late for first responders &amp; night shift.
                  </p>
                  <div className="mt-2.5 pt-2 border-t border-white/10 flex items-center justify-between text-[10px]">
                    <span className="text-gray-400">Put your storefront on the radar</span>
                    <span className="text-[#ffd700] font-extrabold flex items-center gap-1 hover:underline">
                      CLAIM BEACON ($25) &rarr;
                    </span>
                  </div>
                </div>
              )}
            <div 
              key={inc.id}
              onClick={() => onSelect(inc)}
              className={`p-2.5 border rounded cursor-pointer transition-all hover:scale-[1.01]
                ${selectedIncident?.id === inc.id ? 'ring-1' : ''}
                ${highlightedIncidentId === inc.id ? 'animate-pulse bg-yellow-500/20 ring-2 ring-yellow-400' : ''}
              `}
              style={{
                backgroundColor: highlightedIncidentId === inc.id ? 'rgba(234, 179, 8, 0.2)' :
                               inc.threatLevel === ThreatLevel.CRITICAL ? `${theme.danger}15` : 
                               inc.threatLevel === ThreatLevel.HIGH ? '#f9731615' : 
                               'rgba(255,255,255,0.03)',
                borderColor: highlightedIncidentId === inc.id ? '#eab308' :
                            selectedIncident?.id === inc.id ? theme.primary :
                            inc.threatLevel === ThreatLevel.CRITICAL ? `${theme.danger}40` : 
                            inc.threatLevel === ThreatLevel.HIGH ? '#f9731640' : 
                            theme.border,
                boxShadow: highlightedIncidentId === inc.id ? '0 0 15px rgba(234, 179, 8, 0.5)' :
                           selectedIncident?.id === inc.id ? `0 0 8px ${theme.primary}30` : 'none'
              }}
            >
              <div className="flex justify-between items-start mb-1.5">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded text-black uppercase tracking-tighter`}
                  style={{
                    backgroundColor: inc.threatLevel === ThreatLevel.CRITICAL ? theme.danger :
                                   inc.threatLevel === ThreatLevel.HIGH ? '#f97316' : '#d4a843'
                  }}
                >
                  {inc.type}
                </span>
                <div className="flex flex-col items-end">
                  <span className="text-[8px] text-[#d4a843] font-mono flex items-center gap-1">
                    <Clock size={8} /> {new Date(inc.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="text-[7px] text-[#d4a843]/60 font-mono uppercase">Original Intel</span>
                </div>
              </div>
              <p className="text-[11px] text-[#d4a843] leading-snug mb-2 line-clamp-2 font-medium">
                {inc.summary || inc.description}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-[9px] text-[#d4a843]">
                  <MapPin size={9} className="text-[#d4a843]" />
                  <span className="text-[#d4a843]">{inc.distance?.toFixed(2)} mi</span>
                </div>
                <div className="flex items-center gap-2">
                  {(inc.source === 'RADIO' || inc.source === 'DISPATCH') && !inc.summary && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSummarize(inc.id, inc.description);
                      }}
                      disabled={summarizingIncidentId === inc.id || globalBackoffActive}
                      className={`text-[8px] font-mono px-1.5 py-0.5 rounded transition-all flex items-center group relative`}
                      style={{
                        backgroundColor: summarizingIncidentId === inc.id || globalBackoffActive 
                          ? '#1f1f1f' : `${theme.primary}20`,
                        color: summarizingIncidentId === inc.id || globalBackoffActive 
                          ? '#4b5563' : theme.primary,
                        border: `1px solid ${summarizingIncidentId === inc.id || globalBackoffActive ? '#374151' : `${theme.primary}40`}`
                      }}
                    >
                      {summarizingIncidentId === inc.id ? (
                        <Loader2 size={8} className="animate-spin" />
                      ) : (
                        <FileText size={8} className={globalBackoffActive ? 'opacity-30' : ''} />
                      )}
                      <span className="ml-1">{globalBackoffActive ? 'COOLDOWN' : 'INTEL'}</span>
                      {globalBackoffActive && (
                        <div className="absolute inset-0 bg-black/20 animate-pulse rounded" />
                      )}
                    </button>
                  )}
                  <span className="text-[8px] font-mono text-[#d4a843]/80">{inc.source}</span>
                </div>
              </div>
            </div>
          </React.Fragment>
        ))
        )}
        
        {showScrollBottom && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-4 right-4 p-2 rounded-full bg-[#d4a843] text-black shadow-lg hover:bg-[#e6ba55] transition-all z-50 animate-bounce"
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>

      {selectedIncident && (
        <div className="border-t p-3 animate-in slide-in-from-bottom duration-300 shrink-0" style={{ backgroundColor: '#000000', borderColor: '#d4a843' }}>
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-[9px] font-bold uppercase tracking-widest text-[#d4a843]">Detailed Intel</h3>
            <span className="text-[8px] font-mono text-[#d4a843]/70">SIG: {selectedIncident.id.slice(0, 8)}</span>
          </div>
          <div className="space-y-2">
            <p className="text-[10px] text-[#d4a843] leading-tight p-2 rounded border border-[#d4a843]/30 bg-black italic">
              "{selectedIncident.description}"
            </p>
            {vicinityReport && vicinityReport !== "Vicinity analysis unavailable." && vicinityReport !== "Error analyzing vicinity." && vicinityReport !== "Analyzing neighborhood heat..." && (
              <div className="text-[9px] font-mono p-2 rounded border leading-tight bg-black text-[#d4a843] border-[#d4a843]/50">
                <div className="font-bold mb-1 border-b border-[#d4a843]/30 pb-1 uppercase tracking-tighter">Vicinity Analysis</div>
                {vicinityReport}
              </div>
            )}

            {currentUserId && (
              <div className="flex gap-2 items-center">
                <button
                  onClick={async () => {
                    if (!selectedIncident) return;
                    setIsVerifying(true);
                    try {
                      const result = await verifyIncidentLocation(selectedIncident);
                      setVerification(result);
                    } catch (e) {
                      console.error(e);
                      setVerification({ text: "Error verifying location.", sources: [] });
                    } finally {
                      setIsVerifying(false);
                    }
                  }}
                  disabled={isVerifying}
                  className="text-[9px] font-mono px-2 py-1 rounded border border-[#d4a843]/50 hover:bg-[#d4a843]/20 flex items-center gap-1 text-[#d4a843]"
                >
                  {isVerifying ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle size={10} />}
                  VERIFY LOCATION
                </button>
                <VerificationControls 
                  incidentId={selectedIncident.id}
                  incidentOwnerId={selectedIncident.ownerId || 'unknown'}
                  currentUserId={currentUserId}
                  userLocation={userLocation ? { latitude: userLocation.lat, longitude: userLocation.lng } : null}
                  incidentLocation={{ latitude: selectedIncident.location.lat, longitude: selectedIncident.location.lng }}
                  verifiedBy={selectedIncident.verifiedBy || []}
                />
                <PhotoUploader onUpload={async (photoURL) => {
                  const incidentRef = doc(db, `users/${selectedIncident.ownerId || 'unknown'}/incidents/${selectedIncident.id}`);
                  await updateDoc(incidentRef, { photoURL });
                }} />
              </div>
            )}
            
            {verification && (
              <div className="text-[9px] font-mono p-2 rounded border border-[#d4a843]/30 bg-black text-[#d4a843]">
                <div className="font-bold mb-1 uppercase tracking-tighter text-[#d4a843]">Verification Result</div>
                <p className="mb-2">{verification.text}</p>
                {verification.sources.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {verification.sources.map((source, i) => (
                      <a key={i} href={source.web?.uri} target="_blank" rel="noopener noreferrer" className="text-[#d4a843] underline hover:text-[#e6ba55]">
                        {source.web?.title || 'Source'}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <IncidentComments incidentId={selectedIncident.id} />
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentLog;
